import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import {
  SignalButton,
  SignalDropdown,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { CreateWorkspaceInvitationsApiResponse } from '@core/api/workspace/models';
import { CreateWorkspaceInvitationsRequestRole } from '@core/api/workspace/models/createWorkspaceInvitationsRequestRole';
import type { CreatedWorkspaceInvitationResponse } from '@core/api/workspace/models/createdWorkspaceInvitationResponse';
import type { SkippedWorkspaceInvitationResponse } from '@core/api/workspace/models/skippedWorkspaceInvitationResponse';

export interface InviteMemberOverlayResult {
  invitations: CreatedWorkspaceInvitationResponse[];
  skipped: SkippedWorkspaceInvitationResponse[];
}

const ROLE_OPTIONS: {
  label: string;
  value: CreateWorkspaceInvitationsRequestRole;
}[] = [
  { label: 'Viewer', value: CreateWorkspaceInvitationsRequestRole.VIEWER },
  { label: 'Editor', value: CreateWorkspaceInvitationsRequestRole.EDITOR },
  { label: 'Approver', value: CreateWorkspaceInvitationsRequestRole.APPROVER },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-invite-member-overlay',
  imports: [SignalButton, SignalDropdown, SignalTextField],
  templateUrl: './invite-member-overlay.html',
  styleUrl: './invite-member-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteMemberOverlayComponent extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly dialogRef = inject(DialogRef<InviteMemberOverlayResult | null>);

  readonly inviteState = new State<CreateWorkspaceInvitationsApiResponse>();
  readonly email = signal('');
  readonly selectedRole = signal<CreateWorkspaceInvitationsRequestRole>(
    CreateWorkspaceInvitationsRequestRole.VIEWER,
  );
  readonly roleOptions = ROLE_OPTIONS;

  close(): void {
    this.dialogRef.close(null);
  }

  onRoleChange(role: unknown): void {
    this.selectedRole.set(role as CreateWorkspaceInvitationsRequestRole);
  }

  onSendInvite(): void {
    const email = this.email().trim().toLowerCase();
    if (!email || !EMAIL_PATTERN.test(email)) {
      this.toasterService.error('Enter a valid email address.');
      return;
    }

    this.executeRequest({
      state: this.inviteState,
      request: this.userAPIService.createWorkspaceInvitations({
        emails: [email],
        role: this.selectedRole(),
      }),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        const { invitations, skipped } = response.data;
        if (invitations.length > 0) {
          this.toasterService.success('Invitation sent.');
        } else if (skipped.length > 0) {
          this.toasterService.error(skipped[0]?.reason ?? 'Could not send invitation.');
        }
        this.dialogRef.close({ invitations, skipped });
      },
    });
  }
}
